import React, { createContext, useCallback, useContext } from "react";

type FormContextState = {
  form: React.RefObject<HTMLFormElement | null>;
  submitForm: () => void;
};

const FormContext = createContext<FormContextState | null>(null);

if (process.env.NODE_ENV === "development") {
  FormContext.displayName = "FormContext";
}

type FormProviderProps = {
  form: React.RefObject<HTMLFormElement | null>;
  children:
    | ((methods: {
        submitForm: () => void;
        form: React.RefObject<HTMLFormElement | null>;
      }) => React.ReactElement)
    | React.ReactNode;
};

const FormProvider = ({
  children,
  form,
}: FormProviderProps): React.ReactElement => {
  const submitForm = useCallback(
    () =>
      form.current?.dispatchEvent(
        new Event("submit", { cancelable: true, bubbles: true })
      ),
    [form]
  );

  return (
    <FormContext.Provider value={{ form, submitForm }}>
      {typeof children === "function"
        ? children({ submitForm, form })
        : children}
    </FormContext.Provider>
  );
};

const useFormReference = (): FormContextState => {
  const context = useContext(FormContext);

  if (context === null) {
    throw new Error("useFormReference must be used within a FormProvider");
  }

  return context;
};

export { FormProvider, useFormReference };
